import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { marketingApi } from '../api/endpoints';
import KPICard from '../components/KPICard';
import TimeSeriesChart from '../components/charts/TimeSeriesChart';
import PieChart from '../components/charts/PieChart';
import { useTheme } from '../theme/ThemeContext';
import { DEFAULT_START_DATE, todayDateISO } from '../utils/defaultDateFilters';
import LoadingWait from '../components/LoadingWait';

const MAIN_TABS = ['Лиды', 'Веб-аналитика', 'Интернет магазин', 'Соц сети', 'Блогеры', 'E-mail'];
const SOCIAL_TAB = 3;
const BLOGGERS_TAB = 4;
const EMAIL_TAB = 5;

const LEADS_PERIOD_PRESETS = [
  {
    key: 'y2026',
    label: '2026',
    get: () => ({ start_date: DEFAULT_START_DATE, end_date: todayDateISO() }),
  },
  {
    key: 'y2025',
    label: '2025',
    get: () => ({ start_date: '2025-01-01', end_date: '2025-12-31' }),
  },
  {
    key: 'y2024',
    label: '2024',
    get: () => ({ start_date: '2024-01-01', end_date: '2024-12-31' }),
  },
];


export default function MarketingDashboard() {
  const [mainTab, setMainTab] = useState(0);

  const [webAnalytics, setWebAnalytics] = useState([]);
  const [internetShop, setInternetShop] = useState(null);
  const [internetShopLoading, setInternetShopLoading] = useState(false);
  const [leadsAmo, setLeadsAmo] = useState(null);
  const [leadsAmoLoading, setLeadsAmoLoading] = useState(false);
  const [leadsFilters, setLeadsFilters] = useState(() => ({
    start_date: DEFAULT_START_DATE,
    end_date: todayDateISO(),
  }));
  const [social, setSocial] = useState(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [email, setEmail] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);

  const [loading, setLoading] = useState(false);

  const loadWeb = useCallback(async () => {
    setLoading(true);
    try {
      const wa = await marketingApi.webAnalytics({});
      setWebAnalytics(wa.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadWeb(); }, [loadWeb]);

  useEffect(() => {
    if (mainTab !== 2) return;
    if (internetShop !== null) return;
    setInternetShopLoading(true);
    marketingApi
      .internetShop()
      .then((r) => setInternetShop(r.data))
      .catch(() => setInternetShop({ data: [], months: [] }))
      .finally(() => setInternetShopLoading(false));
  }, [mainTab, internetShop]);

  useEffect(() => {
    if (mainTab !== SOCIAL_TAB && mainTab !== BLOGGERS_TAB) return;
    if (social !== null) return;
    setSocialLoading(true);
    marketingApi
      .socialNetworks()
      .then((r) => setSocial(r.data))
      .catch(() => setSocial({ networks: [], companies: [], bloggers: null, error: 'Ошибка загрузки' }))
      .finally(() => setSocialLoading(false));
  }, [mainTab, social]);

  useEffect(() => {
    if (mainTab !== EMAIL_TAB) return;
    if (email !== null) return;
    setEmailLoading(true);
    marketingApi
      .email()
      .then((r) => setEmail(r.data))
      .catch(() => setEmail({ months: [], source_files: [], updated_at: null }))
      .finally(() => setEmailLoading(false));
  }, [mainTab, email]);

  useEffect(() => {
    if (mainTab !== 0) return;
    setLeadsAmoLoading(true);
    marketingApi
      .leadsAmo(leadsFilters)
      .then((r) => setLeadsAmo(r.data))
      .catch(() =>
        setLeadsAmo({
          totals: { new_contacts: 0 },
          monthly: [],
          by_interest: [],
          by_funnel: [],
          by_channel: [],
          monthly_by_interest: {},
          monthly_by_channel: {},
        }),
      )
      .finally(() => setLeadsAmoLoading(false));
  }, [mainTab, leadsFilters]);

  const channelPieData = useMemo(() => {
    return (leadsAmo?.by_channel || [])
      .filter((d) => d.name && d.value > 0)
      .map((d) => ({
        name: d.name,
        value: d.value,
        dynamicsMetric: 'leads',
      }));
  }, [leadsAmo]);

  const channelDynamicsMap = useMemo(() => {
    const out = {};
    const monthly = leadsAmo?.monthly_by_channel || {};
    Object.keys(monthly).forEach((name) => {
      const rows = monthly[name] || [];
      out[name] = rows.map((r) => ({
        date: r.label || r.month,
        leads: r.leads || 0,
      }));
    });
    return out;
  }, [leadsAmo]);

  const imData = internetShop?.data || [];

  const imChartLabel = (r) => String(r.label || r.month || '').replace(/^ИТОГ\s+/i, '');

  const imMoneyChart = useMemo(
    () =>
      imData.map((r) => ({
        date: imChartLabel(r),
        revenue: r.revenue ?? null,
        ad_revenue: r.ad_revenue ?? null,
        spend: r.spend ?? null,
      })),
    [imData],
  );

  const imVolumeChart = useMemo(
    () =>
      imData.map((r) => ({
        date: imChartLabel(r),
        orders: r.orders ?? null,
        leads: r.leads ?? null,
        new_customers: r.new_customers ?? null,
        repeat_purchases: r.repeat_purchases ?? null,
      })),
    [imData],
  );

  const updateLeadsFilter = (key, val) => setLeadsFilters((prev) => ({ ...prev, [key]: val }));

  return (
    <div style={styles.page} className="dashboard-panel-enter">
      <div style={styles.pageHead}>
        <h1 style={styles.h1}>Маркетинг</h1>
      </div>

      <div className="dash-main-tabs" role="tablist" aria-label="Разделы маркетинга">
        {MAIN_TABS.map((t, i) => (
          <button
            key={t}
            type="button"
            role="tab"
            id={`marketing-tab-${i}`}
            aria-selected={mainTab === i}
            aria-controls={`marketing-panel-${i}`}
            tabIndex={mainTab === i ? 0 : -1}
            onClick={() => setMainTab(i)}
            className={`dash-main-tab${mainTab === i ? ' dash-main-tab--active' : ''}`}
          >
            {t}
          </button>
        ))}
      </div>

      {mainTab === 0 && (
        <div
          id="marketing-panel-0"
          role="tabpanel"
          aria-labelledby="marketing-tab-0"
          aria-busy={leadsAmoLoading}
        >
          <div style={leadsStyles.dateBar}>
            <div style={leadsStyles.dateGroup}>
              <span style={leadsStyles.dateLabel}>Период анализа лидов</span>
              <div style={leadsStyles.dateRow}>
                <input
                  style={leadsStyles.dateInput}
                  type="date"
                  value={leadsFilters.start_date || ''}
                  onChange={(e) => updateLeadsFilter('start_date', e.target.value)}
                  aria-label="Начало периода"
                />
                <span style={leadsStyles.arrow}>→</span>
                <input
                  style={leadsStyles.dateInput}
                  type="date"
                  value={leadsFilters.end_date || ''}
                  onChange={(e) => updateLeadsFilter('end_date', e.target.value)}
                  aria-label="Конец периода"
                />
              </div>
            </div>
            <div style={leadsStyles.presetGroup} role="group" aria-label="Быстрый выбор периода">
              {LEADS_PERIOD_PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  style={leadsStyles.presetBtn}
                  onClick={() => setLeadsFilters((prev) => ({ ...prev, ...p.get() }))}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {leadsAmo?.source?.lids_folder && (
            <div style={leadsStyles.sourceHint}>
              Источник: {leadsAmo.source.lids_folder}
              {leadsAmo.source.deals_files?.length
                ? ` · сделки: ${leadsAmo.source.deals_files.join(', ')}`
                : ''}
            </div>
          )}
          <div style={styles.section} className="dash-section">
            <h2 style={styles.sectionTitle}>Распределение по каналам</h2>
            {leadsAmoLoading ? (
              <LoadingWait />
            ) : (
              <PieChart data={channelPieData} dynamicsMap={channelDynamicsMap} />
            )}
          </div>
          <LeadsAmoPanel
            data={leadsAmo}
            loading={leadsAmoLoading}
            filters={leadsFilters}
          />
        </div>
      )}

      {mainTab === 1 && (
        <div
          style={styles.section}
          className="dash-section"
          id="marketing-panel-1"
          role="tabpanel"
          aria-labelledby="marketing-tab-1"
          aria-busy={loading}
        >
          {loading ? (
            <LoadingWait />
          ) : (
            <>
              <h2 style={styles.h2}>Веб-аналитика (Яндекс.Метрика)</h2>
              {webAnalytics.length === 0 && (
                <div className="empty-state" style={styles.emptyState}>Нет данных Яндекс.Метрики за этот период или счётчики не настроены.</div>
              )}
              <WebAnalyticsTabs sites={webAnalytics} />
            </>
          )}
        </div>
      )}

      {mainTab === 2 && (
        <div
          id="marketing-panel-2"
          role="tabpanel"
          aria-labelledby="marketing-tab-2"
          aria-busy={internetShopLoading}
        >
          {internetShopLoading ? (
            <div style={styles.section} className="dash-section">
              <LoadingWait />
            </div>
          ) : imData.length === 0 ? (
            <div style={styles.section} className="dash-section">
              <h2 style={styles.sectionTitle}>Интернет магазин (План/Факт)</h2>
              <div className="empty-state" style={styles.emptyState}>Нет данных по интернет-магазину.</div>
            </div>
          ) : (
            <>
              <div style={styles.section} className="dash-section">
                <h2 style={styles.sectionTitle}>Выручка и реклама по месяцам</h2>
                <TimeSeriesChart
                  idPrefix="im-money"
                  data={imMoneyChart}
                  height={320}
                  lines={[
                    { key: 'revenue', name: 'Выручка, ₽', color: 'var(--accent)' },
                    { key: 'ad_revenue', name: 'Доход по рекламе, ₽', color: 'var(--success)' },
                    { key: 'spend', name: 'Расходы на рекламу, ₽', color: 'var(--warning)' },
                  ]}
                />
              </div>

              <div style={styles.section} className="dash-section">
                <h2 style={styles.sectionTitle}>Заказы и клиенты по месяцам</h2>
                <TimeSeriesChart
                  idPrefix="im-volume"
                  data={imVolumeChart}
                  height={320}
                  lines={[
                    { key: 'orders', name: 'Заказы', color: 'var(--accent)' },
                    { key: 'leads', name: 'Сделки по рекламе', color: 'var(--warning)' },
                    { key: 'new_customers', name: 'Новые клиенты', color: 'var(--success)' },
                    { key: 'repeat_purchases', name: 'Повторные покупки', color: '#818cf8' },
                  ]}
                />
              </div>

              <div style={styles.section} className="dash-section">
                <h2 style={styles.sectionTitle}>Интернет магазин (План/Факт)</h2>
                <div style={styles.imTableScroll}>
                  <table style={styles.imTable} className="im-zebra">
                    <thead>
                      <tr>
                        <th style={{ ...styles.imTh, ...styles.imThSticky }}>Метрика</th>
                        {imData.map((r) => (
                          <th key={r.month} style={styles.imTh}>{r.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Выручка, ₽', key: 'revenue', fmt: (v) => (v != null ? Math.round(v).toLocaleString('ru-RU') : '—') },
                        { label: 'Заказы', key: 'orders', fmt: (v) => (v != null ? v.toLocaleString('ru-RU') : '—') },
                        { label: 'Расходы на рекламу, ₽', key: 'spend', fmt: (v) => (v != null ? Math.round(v).toLocaleString('ru-RU') : '—') },
                        { label: 'Доход по рекламе, ₽', key: 'ad_revenue', fmt: (v) => (v != null ? Math.round(v).toLocaleString('ru-RU') : '—') },
                        { label: 'Кол-во сделок по рекламе', key: 'leads', fmt: (v) => (v != null ? v : '—') },
                        { label: 'Факт ROMI', key: 'romi', fmt: (v) => (v != null ? `${Number(v).toFixed(2)}%` : '—') },
                        { label: 'Стоимость клиента', key: 'cpl', fmt: (v) => (v != null ? Number(v).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) : '—') },
                        { label: 'Переходы из соц сетей', key: 'transitions', fmt: (v) => (v != null && v !== '' ? v : '—') },
                        { label: 'Количество новых клиентов***', key: 'new_customers', fmt: (v) => (v != null && v !== '' ? Number(v).toLocaleString('ru-RU') : '—') },
                        { label: 'Количество повторных покупок ***', key: 'repeat_purchases', fmt: (v) => (v != null && v !== '' ? Number(v).toLocaleString('ru-RU') : '—') },
                      ].map((metric) => (
                        <tr key={metric.key}>
                          <td style={{ ...styles.imTd, ...styles.imTdSticky }}>{metric.label}</td>
                          {imData.map((r) => (
                            <td key={r.month} style={styles.imTdVal} className="tabular-nums">
                              {metric.fmt(r[metric.key])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {mainTab === SOCIAL_TAB && (
        <div
          style={styles.section}
          className="dash-section"
          id="marketing-panel-3"
          role="tabpanel"
          aria-labelledby="marketing-tab-3"
          aria-busy={socialLoading}
        >
          <h2 style={styles.sectionTitle}>Соц сети</h2>
          {socialLoading ? (
            <LoadingWait />
          ) : !social || !social.networks?.length ? (
            <div className="empty-state" style={styles.emptyState}>
              Нет данных. Положите файлы в папку BazaDann/Соц сети.
            </div>
          ) : (
            <SocialNetworksPanel data={social} />
          )}
        </div>
      )}

      {mainTab === BLOGGERS_TAB && (
        <div
          style={styles.section}
          className="dash-section"
          id="marketing-panel-4"
          role="tabpanel"
          aria-labelledby="marketing-tab-4"
          aria-busy={socialLoading}
        >
          <h2 style={styles.sectionTitle}>Блогеры (PLAS)</h2>
          {socialLoading ? (
            <LoadingWait />
          ) : (
            <BloggersPanel data={social?.bloggers} />
          )}
        </div>
      )}

      {mainTab === EMAIL_TAB && (
        <div
          style={styles.section}
          className="dash-section"
          id="marketing-panel-5"
          role="tabpanel"
          aria-labelledby="marketing-tab-5"
          aria-busy={emailLoading}
        >
          <h2 style={styles.sectionTitle}>E-mail рассылки (Unisender)</h2>
          {emailLoading ? (
            <LoadingWait />
          ) : !email || !email.months?.length ? (
            <div className="empty-state" style={styles.emptyState}>
              Нет данных. Положите выгрузки Unisender в папку BazaDann/Unisender.
            </div>
          ) : (
            <EmailPanel data={email} />
          )}
        </div>
      )}
    </div>
  );
}

function LeadsAmoPanel({ data, loading, filters }) {
  const { theme } = useTheme();
  const [selectedInterest, setSelectedInterest] = useState(null);
  const chartBlockRef = useRef(null);

  useEffect(() => {
    setSelectedInterest(null);
  }, [filters.start_date, filters.end_date]);

  useEffect(() => {
    if (!selectedInterest) return;
    chartBlockRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedInterest]);

  const monthlyByInterest = data?.monthly_by_interest || {};

  const monthlyOverall = useMemo(() => {
    const sd = filters.start_date ? filters.start_date.substring(0, 7) : null;
    const ed = filters.end_date   ? filters.end_date.substring(0, 7)   : null;
    return (data?.monthly || [])
      .filter((m) => {
        if (!m.month) return true;
        if (sd && m.month < sd) return false;
        if (ed && m.month > ed) return false;
        return true;
      })
      .map((m) => ({
        date: m.label || m.month,
        leads: m.leads || 0,
        new_customers: m.new_customers || 0,
        new_contacts: m.new_contacts ?? 0,
      }));
  }, [data?.monthly, filters.start_date, filters.end_date]);

  const monthlyChart = useMemo(() => {
    if (!selectedInterest) {
      return monthlyOverall;
    }
    const series = monthlyByInterest[selectedInterest];
    if (!Array.isArray(series)) {
      return [];
    }
    return series.map((m) => ({
      date: m.label || m.month,
      leads: m.leads || 0,
      new_customers: m.new_customers || 0,
      new_contacts: m.new_contacts ?? 0,
    }));
  }, [selectedInterest, monthlyByInterest, monthlyOverall]);

  const byInterest = (data?.by_interest || [])
    .filter((d) => d.name && d.name.toLowerCase() !== 'не указано')
    .slice(0, 20);
  const byFunnel = (data?.by_funnel || []).slice(0, 12);
  const totals = data?.totals || {};

  const toggleInterestRow = (name) => {
    setSelectedInterest((prev) => (prev === name ? null : name));
  };

  return (
    <>
      <div style={styles.kpiRow}>
        <KPICard
          title="Всего лидов"
          value={totals.leads}
          dimmed={loading}
        />
        <KPICard
          title="Новых клиентов"
          value={totals.new_customers}
          dimmed={loading}
        />
        <KPICard
          title="Новых контактов"
          value={totals.new_contacts}
          dimmed={loading}
        />
        <KPICard
          title="Воронок"
          value={totals.deals_files}
          dimmed={loading}
        />
        <KPICard
          title="Контактов в базе"
          value={totals.contacts_loaded}
          dimmed={loading}
        />
      </div>

      {loading ? (
        <div style={styles.section} className="dash-section">
          <LoadingWait />
        </div>
      ) : (
        <>
          <div ref={chartBlockRef} style={styles.section} className="dash-section">
            <h2 style={styles.sectionTitle}>Лиды, новые клиенты и новые контакты по месяцам</h2>
            {selectedInterest ? (
              <div
                style={{
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  По интересу:{' '}
                  <strong style={{ color: 'var(--accent)' }}>{selectedInterest}</strong>
                </span>
                <button
                  type="button"
                  className="dash-toolbar-btn"
                  onClick={() => setSelectedInterest(null)}
                  style={{
                    background: 'rgba(34,211,238,.12)',
                    border: `1px solid rgba(34,211,238,.35)`,
                    color: 'var(--accent)',
                    borderRadius: 8,
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Все интересы
                </button>
              </div>
            ) : (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 10 }}>
                Выберите интерес в таблице «Детализация» ниже — график и помесячная таблица переключатся на этот срез.
              </div>
            )}
            <TimeSeriesChart
              data={monthlyChart}
              lines={[
                { key: 'leads', name: 'Лиды', color: theme.chartColors[3] },
                { key: 'new_customers', name: 'Новые клиенты', color: theme.chartColors[1] },
                { key: 'new_contacts', name: 'Новые контакты', color: theme.chartColors[2] },
              ]}
              height={320}
            />
          </div>

          <div style={styles.grid}>
            <div style={styles.section} className="dash-section">
              <h2 style={styles.sectionTitle}>Распределение по «Интерес к»</h2>
              <PieChart data={byInterest} />
            </div>
            <div style={styles.section} className="dash-section">
              <h2 style={styles.sectionTitle}>Лиды по воронкам</h2>
              <PieChart data={byFunnel} />
            </div>
          </div>

          <div style={styles.section} className="dash-section">
            <h2 style={styles.sectionTitle}>Детализация: «Интерес к»</h2>
            {byInterest.length === 0 ? (
              <div className="empty-state" style={styles.emptyState}>
                Нет данных по «Интерес к» за выбранный период.
              </div>
            ) : (
              <>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 12 }}>
                  Нажмите на строку — выше покажется динамика лидов, новых клиентов и новых контактов только по этому интересу. Повторный клик снимает выбор.
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={styles.imTable}>
                    <thead>
                      <tr>
                        <th style={styles.imTh}>Интерес</th>
                        <th style={styles.imTh}>Кол-во лидов</th>
                        <th style={styles.imTh}>Доля</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const total = byInterest.reduce((s, r) => s + r.value, 0) || 1;
                        return byInterest.map((r) => {
                          const sel = selectedInterest === r.name;
                          return (
                            <tr
                              key={r.name}
                              role="button"
                              tabIndex={0}
                              aria-pressed={sel}
                              onClick={() => toggleInterestRow(r.name)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleInterestRow(r.name);
                                }
                              }}
                              style={{
                                cursor: 'pointer',
                                outline: 'none',
                                background: sel ? 'rgba(34,211,238,.14)' : undefined,
                                boxShadow: sel ? 'inset 3px 0 0 var(--accent)' : undefined,
                              }}
                            >
                              <td style={styles.imTd}>{r.name}</td>
                              <td style={styles.imTdVal}>
                                {r.value.toLocaleString('ru-RU')}
                              </td>
                              <td style={styles.imTdVal}>
                                {((r.value / total) * 100).toFixed(1)}%
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>

          <div style={styles.section} className="dash-section">
            <h2 style={styles.sectionTitle}>
              Помесячная статистика
              {selectedInterest ? (
                <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
                  {' '}
                  — {selectedInterest}
                </span>
              ) : null}
            </h2>
            {monthlyChart.length === 0 ? (
              <div className="empty-state" style={styles.emptyState}>Нет данных за выбранный период.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={styles.imTable}>
                  <thead>
                    <tr>
                      <th style={styles.imTh}>Месяц</th>
                      <th style={styles.imTh}>Лиды</th>
                      <th style={styles.imTh}>Новые клиенты</th>
                      <th style={styles.imTh}>Новые контакты</th>
                      <th style={styles.imTh}>Конверсия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyChart.map((r) => (
                      <tr key={r.date}>
                        <td style={styles.imTd}>{r.date}</td>
                        <td style={styles.imTdVal}>
                          {r.leads.toLocaleString('ru-RU')}
                        </td>
                        <td style={styles.imTdVal}>
                          {r.new_customers.toLocaleString('ru-RU')}
                        </td>
                        <td style={styles.imTdVal}>
                          {(r.new_contacts ?? 0).toLocaleString('ru-RU')}
                        </td>
                        <td style={styles.imTdVal}>
                          {r.leads > 0
                            ? `${((r.new_customers / r.leads) * 100).toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}

// ── Email Panel (Unisender) ──────────────────────────────────────────────────

const EMAIL_SORT_OPTIONS = [
  { key: 'open_rate', label: 'Открываемость' },
  { key: 'ctr', label: 'CTR' },
  { key: 'sent', label: 'Отправлено' },
  { key: 'clicked', label: 'Переходы' },
];

function EmailPanel({ data }) {
  const months = data.months || [];
  const [activeMonth, setActiveMonth] = useState(Math.max(0, months.length - 1));
  const [sortBy, setSortBy] = useState('open_rate');

  const month = months[activeMonth] || months[months.length - 1];
  if (!month) return null;

  const s = month.summary || {};
  const deltas = month.deltas || {};
  const prevMonth = activeMonth > 0 ? months[activeMonth - 1] : null;

  const fmtPct = (v) => (v != null ? `${Number(v).toFixed(v >= 10 ? 0 : 1)}%` : '—');
  const fmtNum = (v) => (v != null ? Number(v).toLocaleString('ru-RU') : '—');

  const DeltaBadge = ({ field, invert = false }) => {
    const d = deltas[field];
    if (d == null) return <span style={eStyles.deltaMuted}>—</span>;
    const positive = invert ? d < 0 : d > 0;
    const negative = invert ? d > 0 : d < 0;
    const color = positive ? 'var(--success)' : negative ? 'var(--danger)' : 'var(--text-muted)';
    const arrow = d > 0 ? '↑' : d < 0 ? '↓' : '→';
    return (
      <span style={{ ...eStyles.delta, color }}>
        {arrow} {d > 0 ? '+' : ''}{d}%
      </span>
    );
  };

  const kpiCards = [
    {
      title: 'Открываемость',
      value: fmtPct(s.open_rate),
      sub: `${fmtNum(s.opened)} прочтений`,
      delta: 'open_rate',
      primary: true,
    },
    {
      title: 'Отправлено',
      value: fmtNum(s.sent),
      sub: '100%',
      delta: 'sent',
    },
    {
      title: 'Доставлено',
      value: fmtNum(s.delivered),
      sub: fmtPct(s.delivery_rate),
      delta: 'delivered',
    },
    {
      title: 'Прочтения',
      value: fmtNum(s.opened),
      sub: fmtPct(s.open_rate),
      delta: 'opened',
    },
    {
      title: 'CTR',
      value: fmtPct(s.ctr),
      sub: `${fmtNum(s.clicked)} переходов`,
      delta: 'ctr',
    },
    {
      title: 'Переходы по ссылкам',
      value: fmtNum(s.clicked),
      sub: fmtPct(s.ctr),
      delta: 'clicked',
    },
    {
      title: 'uCTR',
      value: fmtPct(s.uctr),
      sub: 'уникальные клики',
      delta: 'uctr',
    },
    {
      title: 'Отписки',
      value: fmtNum(s.unsubscribed),
      sub: fmtPct(s.unsubscribe_rate),
      delta: 'unsubscribed',
      invertDelta: true,
    },
  ];

  const chartData = (month.timeline || []).map((pt) => ({
    date: pt.date,
    sent: pt.sent,
    opened: pt.opened,
  }));

  const sortedCampaigns = [...(month.campaigns || [])].sort(
    (a, b) => (b[sortBy] || 0) - (a[sortBy] || 0),
  );

  const formatSendDate = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw.replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString('ru-RU', {
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div>
      {/* Month tabs */}
      <div style={eStyles.monthRow} role="tablist" aria-label="Месяцы">
        {months.map((m, i) => (
          <button
            key={m.key}
            type="button"
            role="tab"
            aria-selected={activeMonth === i}
            onClick={() => setActiveMonth(i)}
            style={{ ...eStyles.monthBtn, ...(activeMonth === i ? eStyles.monthBtnActive : {}) }}
          >
            {m.label}
            <span style={eStyles.monthCount}>{m.summary?.campaigns_count || 0} рассылок</span>
          </button>
        ))}
      </div>

      {prevMonth && (
        <div style={eStyles.compareNote}>
          по сравнению с {prevMonth.label.toLowerCase()}
        </div>
      )}

      {/* KPI grid — как в Unisender analytics */}
      <h3 style={eStyles.blockTitle}>Показатели рассылок</h3>
      <div style={eStyles.kpiGrid}>
        {kpiCards.map((k) => (
          <div
            key={k.title}
            style={{
              ...eStyles.kpiCard,
              ...(k.primary ? eStyles.kpiCardPrimary : {}),
            }}
          >
            <div style={eStyles.kpiCardHead}>
              <span style={eStyles.kpiTitle}>{k.title}</span>
              {k.primary && <span style={eStyles.kpiBadge}>Основной KPI</span>}
            </div>
            <div style={eStyles.kpiValueRow}>
              <span style={eStyles.kpiValue}>{k.value}</span>
              {k.sub && <span style={eStyles.kpiSub}>{k.sub}</span>}
            </div>
            <DeltaBadge field={k.delta} invert={k.invertDelta} />
          </div>
        ))}
      </div>

      {/* Timeline chart */}
      {chartData.length > 0 && (
        <div style={eStyles.chartBlock}>
          <h3 style={eStyles.blockTitle}>Данные о рассылках</h3>
          <TimeSeriesChart
            data={chartData}
            lines={[
              { key: 'sent', name: 'Отправлено', color: '#a78bfa' },
              { key: 'opened', name: 'Прочтения', color: 'var(--accent)' },
            ]}
            height={280}
          />
        </div>
      )}

      {/* Top campaigns */}
      <div style={eStyles.topBlock}>
        <div style={eStyles.topHead}>
          <h3 style={{ ...eStyles.blockTitle, margin: 0 }}>Топ рассылок</h3>
          <div style={eStyles.sortWrap}>
            <span style={eStyles.sortLabel}>Лучшие по параметру</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={eStyles.sortSelect}
              aria-label="Сортировка рассылок"
            >
              {EMAIL_SORT_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={eStyles.campaignList}>
          {sortedCampaigns.map((c) => (
            <div key={`${c.id}-${c.send_date}`} style={eStyles.campaignCard}>
              <div style={eStyles.campaignMain}>
                <div style={eStyles.campaignIcon}>✉</div>
                <div style={eStyles.campaignInfo}>
                  <div style={eStyles.campaignName}>{c.name}</div>
                  <div style={eStyles.campaignDate}>
                    Отправлено {formatSendDate(c.send_date)}
                  </div>
                </div>
              </div>
              <div style={eStyles.campaignMetrics}>
                {[
                  { label: 'Отправлено', pct: '100%', val: c.sent },
                  { label: 'Доставлено', pct: `${c.delivery_rate}%`, val: c.delivered },
                  { label: 'Прочтения', pct: `${c.open_rate}%`, val: c.open_unique || c.opened },
                  { label: 'Переходы', pct: `${c.ctr}%`, val: c.clicked },
                ].map((m) => (
                  <div key={m.label} style={eStyles.metricCol}>
                    <div style={eStyles.metricPctRow}>
                      <span style={eStyles.metricPct}>{m.pct}</span>
                      <span style={eStyles.metricDot}>·</span>
                      <span style={eStyles.metricVal}>{fmtNum(m.val)}</span>
                    </div>
                    <div style={eStyles.metricLabel}>{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.updated_at && (
        <div style={eStyles.footer}>
          Источник: BazaDann/Unisender · обновлено {data.updated_at}
        </div>
      )}
    </div>
  );
}

const eStyles = {
  monthRow: {
    display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16,
  },
  monthBtn: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
    background: 'var(--bg-input)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', borderRadius: 10,
    padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    transition: 'all .15s',
  },
  monthBtnActive: {
    background: 'var(--accent-dim)', color: 'var(--accent)',
    borderColor: 'rgba(34,211,238,.35)',
    boxShadow: '0 0 16px rgba(34,211,238,.1)',
  },
  monthCount: { fontSize: 10, fontWeight: 500, color: 'var(--text-muted)' },
  compareNote: {
    color: 'var(--text-muted)', fontSize: 12, marginBottom: 18,
  },
  blockTitle: {
    color: 'var(--text-primary)', fontSize: 15, fontWeight: 700,
    margin: '0 0 14px 0',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 12, marginBottom: 28,
  },
  kpiCard: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 14, padding: '16px 18px',
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  kpiCardPrimary: {
    borderColor: 'rgba(167,139,250,.4)',
    background: 'linear-gradient(145deg, rgba(167,139,250,.12) 0%, var(--bg-input) 100%)',
  },
  kpiCardHead: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  kpiTitle: { color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 },
  kpiBadge: {
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
    color: '#c4b5fd', background: 'rgba(167,139,250,.2)',
    borderRadius: 6, padding: '2px 7px',
  },
  kpiValueRow: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  kpiValue: { color: 'var(--text-primary)', fontSize: 26, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  kpiSub: { color: 'var(--text-muted)', fontSize: 13 },
  delta: { fontSize: 12, fontWeight: 600 },
  deltaMuted: { color: 'var(--text-muted)', fontSize: 12 },
  chartBlock: {
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 14, padding: '18px 20px', marginBottom: 28,
  },
  topBlock: { marginBottom: 12 },
  topHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12, marginBottom: 14,
  },
  sortWrap: { display: 'flex', alignItems: 'center', gap: 10 },
  sortLabel: { color: 'var(--text-muted)', fontSize: 12 },
  sortSelect: {
    background: 'var(--bg-input)', color: 'var(--text-primary)',
    border: '1px solid var(--border)', borderRadius: 8,
    padding: '7px 12px', fontSize: 12, outline: 'none',
  },
  campaignList: { display: 'flex', flexDirection: 'column', gap: 10 },
  campaignCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 16,
    background: 'var(--bg-input)',
    border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 18px',
    transition: 'border-color .15s',
  },
  campaignMain: { display: 'flex', alignItems: 'flex-start', gap: 12, flex: '1 1 280px', minWidth: 0 },
  campaignIcon: {
    width: 36, height: 44, borderRadius: 6,
    background: 'linear-gradient(160deg, rgba(34,211,238,.15) 0%, rgba(167,139,250,.15) 100%)',
    border: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, flexShrink: 0,
  },
  campaignInfo: { minWidth: 0 },
  campaignName: {
    color: 'var(--text-primary)', fontSize: 14, fontWeight: 600,
    lineHeight: 1.35, marginBottom: 4,
  },
  campaignDate: { color: 'var(--text-muted)', fontSize: 11 },
  campaignMetrics: {
    display: 'flex', flexWrap: 'wrap', gap: 20,
    flex: '0 1 auto',
  },
  metricCol: { minWidth: 90 },
  metricPctRow: { display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 2 },
  metricPct: { color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  metricDot: { color: 'var(--text-muted)', fontSize: 12 },
  metricVal: { color: 'var(--text-muted)', fontSize: 12, fontVariantNumeric: 'tabular-nums' },
  metricLabel: { color: 'var(--text-muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 },
  footer: { color: 'var(--text-muted)', fontSize: 11, marginTop: 16, textAlign: 'right' },
};

// ── Social Networks Panel ────────────────────────────────────────────────────

const PLAS_RECORD_COLS = [
  { key: 'period', label: 'Период' },
  { key: 'subscribers', label: 'Подписчики' },
  { key: 'growth', label: 'Прирост' },
  { key: 'content_views', label: 'Просмотры контента' },
  { key: 'post_reach', label: 'Охват постов' },
  { key: 'reposts', label: 'Репосты' },
  { key: 'post_views', label: 'Просмотры постов' },
  { key: 'comments', label: 'Комментарии' },
  { key: 'likes', label: 'Лайки' },
];

/** Поля records → имена как в Instagram monthly (для KPI/Динамика) */
const RECORD_TO_MONTHLY = [
  { key: 'subscribers', name: 'Подписчики' },
  { key: 'content_views', name: 'Просмотры' },
  { key: 'post_views', name: 'Просмотры постов' },
  { key: 'post_reach', name: 'Охваты' },
  { key: 'likes', name: 'Лайки' },
  { key: 'comments', name: 'Комментарии' },
  { key: 'reposts', name: 'Репосты' },
  { key: 'growth', name: 'Прирост' },
];

function recordsToMonthly(records) {
  const list = records || [];
  if (!list.length) return null;
  const months = list.map((r) => String(r.period || '').trim() || '—');
  const rows = [];
  for (const { key, name } of RECORD_TO_MONTHLY) {
    const values = list.map((r) => {
      const v = r[key];
      return typeof v === 'number' ? v : (v == null || v === '' ? null : Number(v));
    });
    if (values.every((v) => v == null || Number.isNaN(v))) continue;
    rows.push({
      name,
      values: values.map((v) => (v != null && !Number.isNaN(v) ? v : null)),
      is_pct: false,
    });
  }
  // Если есть лайки+комменты — синтетические «Взаимодействия»
  const likes = rows.find((r) => r.name === 'Лайки');
  const comments = rows.find((r) => r.name === 'Комментарии');
  if (likes && comments && !rows.some((r) => r.name === 'Взаимодействия')) {
    rows.push({
      name: 'Взаимодействия',
      values: likes.values.map((v, i) => {
        const a = v ?? 0;
        const b = comments.values[i] ?? 0;
        return (v == null && comments.values[i] == null) ? null : a + b;
      }),
      is_pct: false,
    });
  }
  // Просмотры: если только «Просмотры постов» / контент — для графика нужен ключ «Просмотры»
  if (!rows.some((r) => r.name === 'Просмотры')) {
    const alt = rows.find((r) => r.name === 'Просмотры постов');
    if (alt) rows.unshift({ name: 'Просмотры', values: [...alt.values], is_pct: false });
  }
  if (!rows.length) return null;
  return { months, rows };
}

function fmtSocialVal(v) {
  if (v == null || v === '') return '—';
  if (typeof v === 'number') {
    if (Number.isInteger(v)) return v.toLocaleString('ru-RU');
    return v.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
  }
  return String(v);
}

function fmtEr(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return String(v);
  // В файле ER как доля (0.16 → 16%)
  const pct = n <= 1 ? n * 100 : n;
  return `${pct.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`;
}

function erToPct(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (Number.isNaN(n)) return null;
  return n <= 1 ? Math.round(n * 1000) / 10 : n;
}

function BloggersPanel({ data }) {
  const { theme } = useTheme();
  const rows = data?.rows || [];
  if (!rows.length) {
    return (
      <div className="empty-state" style={styles.emptyState}>
        Нет данных по блогерам. Нужен лист «блоггеры» в файле PLAS (Соц сети).
      </div>
    );
  }

  const totals = rows.reduce(
    (acc, r) => {
      acc.publications += r.publications || 0;
      acc.views += r.views || 0;
      return acc;
    },
    { publications: 0, views: 0 },
  );

  const periods = [];
  const periodSeen = new Set();
  rows.forEach((r) => {
    const p = String(r.period || '').trim() || '—';
    if (!periodSeen.has(p)) {
      periodSeen.add(p);
      periods.push(p);
    }
  });
  const channels = [];
  const channelSeen = new Set();
  rows.forEach((r) => {
    const ch = String(r.channel || '').trim() || '—';
    if (!channelSeen.has(ch)) {
      channelSeen.add(ch);
      channels.push(ch);
    }
  });

  const erChartData = periods.map((period) => {
    const pt = { date: period };
    channels.forEach((ch) => {
      const row = rows.find(
        (r) => (String(r.period || '').trim() || '—') === period
          && (String(r.channel || '').trim() || '—') === ch,
      );
      pt[ch] = erToPct(row?.er);
    });
    return pt;
  });
  const erChartLines = channels.map((ch, i) => ({
    key: ch,
    name: `${ch.charAt(0).toUpperCase()}${ch.slice(1)} ER, %`,
    color: theme.chartColors[i % theme.chartColors.length],
  }));

  return (
    <div>
      <div style={tabStyles.kpiStrip}>
        <div style={tabStyles.kpiBox}>
          <span style={tabStyles.kpiLabel}>Публикации</span>
          <span style={tabStyles.kpiValue}>{totals.publications.toLocaleString('ru-RU')}</span>
        </div>
        <div style={tabStyles.kpiBox}>
          <span style={tabStyles.kpiLabel}>Просмотры</span>
          <span style={tabStyles.kpiValue}>{Math.round(totals.views).toLocaleString('ru-RU')}</span>
        </div>
        <div style={tabStyles.kpiBox}>
          <span style={tabStyles.kpiLabel}>Каналы</span>
          <span style={tabStyles.kpiValue}>{channels.length}</span>
        </div>
      </div>

      {erChartData.length > 0 && erChartLines.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={iStyles.subHead}>Динамика ER</h3>
          <TimeSeriesChart
            data={erChartData}
            lines={erChartLines}
            height={280}
            idPrefix="bloggers-er"
          />
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={iStyles.table}>
          <thead>
            <tr>
              <th style={{ ...iStyles.th, textAlign: 'left' }}>Период</th>
              <th style={{ ...iStyles.th, textAlign: 'left' }}>Канал</th>
              <th style={{ ...iStyles.th, textAlign: 'right' }}>Публикации</th>
              <th style={{ ...iStyles.th, textAlign: 'right' }}>Просмотры</th>
              <th style={{ ...iStyles.th, textAlign: 'right' }}>ER</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.channel}-${r.period}-${i}`}>
                <td style={iStyles.td}>{r.period || '—'}</td>
                <td style={{ ...iStyles.td, fontWeight: 600, textTransform: 'capitalize' }}>
                  {r.channel || '—'}
                </td>
                <td style={iStyles.tdVal}>{fmtSocialVal(r.publications)}</td>
                <td style={iStyles.tdVal}>{fmtSocialVal(r.views)}</td>
                <td style={iStyles.tdVal}>{fmtEr(r.er)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.source && <div style={socStyles.source}>Источник: {data.source}</div>}
    </div>
  );
}

function SocialCompanyView({ company }) {
  if (!company) {
    return <div className="empty-state" style={styles.emptyState}>Нет данных по этой компании.</div>;
  }

  if (company.kind === 'monthly') {
    return (
      <InstagramPanel
        data={{
          accounts: [{ name: company.label, months: company.months, rows: company.rows }],
          months: company.months,
          rows: company.rows,
        }}
      />
    );
  }

  if (company.kind === 'snapshot' || company.kind === 'metrics') {
    return (
      <div>
        {company.period && (
          <div style={socStyles.period}>Период: {company.period}</div>
        )}
        <div style={socStyles.metricGrid}>
          {(company.metrics || []).map((m) => (
            <div key={m.name} style={socStyles.metricCard}>
              <div style={socStyles.metricName}>{m.name}</div>
              <div style={socStyles.metricValue}>{fmtSocialVal(m.value)}</div>
              {m.delta && <div style={socStyles.metricDelta}>{m.delta}</div>}
            </div>
          ))}
        </div>
        {company.note && <p style={socStyles.note}>{company.note}</p>}
        {company.source && <div style={socStyles.source}>Источник: {company.source}</div>}
      </div>
    );
  }

  if (company.kind === 'records') {
    const records = company.records || [];
    const monthly = recordsToMonthly(records);
    const usedCols = PLAS_RECORD_COLS.filter(
      (c) => c.key === 'period' || records.some((r) => r[c.key] != null && r[c.key] !== '')
    );

    return (
      <div>
        {monthly && monthly.months.length >= 1 && (
          <div style={{ marginBottom: 8 }}>
            <InstagramPanel
              data={{
                accounts: [{ name: company.label, months: monthly.months, rows: monthly.rows }],
                months: monthly.months,
                rows: monthly.rows,
              }}
            />
          </div>
        )}
        {/* Исходная таблица периодов — если динамика уже показала те же цифры в другом виде, не дублируем полностью;
            оставляем компактную сводку только когда monthly не собрался */}
        {!monthly && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={iStyles.table}>
                <thead>
                  <tr>
                    {usedCols.map((c) => (
                      <th key={c.key} style={{ ...iStyles.th, textAlign: c.key === 'period' ? 'left' : 'right' }}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {records.map((r, i) => (
                    <tr key={i}>
                      {usedCols.map((c) => (
                        <td
                          key={c.key}
                          style={c.key === 'period' ? iStyles.td : iStyles.tdVal}
                        >
                          {fmtSocialVal(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {company.source && <div style={socStyles.source}>Источник: {company.source}</div>}
      </div>
    );
  }

  return <div className="empty-state" style={styles.emptyState}>Неизвестный формат данных.</div>;
}

function SocialNetworksPanel({ data }) {
  const networks = (data.networks || []).filter((n) => (n.companies || []).length > 0);
  const [netIdx, setNetIdx] = useState(0);
  const network = networks[netIdx] || networks[0];
  const companies = network?.companies || [];
  const [coIdx, setCoIdx] = useState(0);

  useEffect(() => {
    setCoIdx(0);
  }, [netIdx]);

  useEffect(() => {
    if (coIdx >= companies.length) setCoIdx(0);
  }, [companies.length, coIdx]);

  if (!networks.length) {
    return (
      <div className="empty-state" style={styles.emptyState}>
        {data.error || 'Нет данных по соцсетям.'}
      </div>
    );
  }

  const company = companies[coIdx] || companies[0];

  return (
    <div>
      <div style={tabStyles.siteTabRow} role="tablist" aria-label="Соцсети">
        {networks.map((n, i) => (
          <button
            key={n.id}
            type="button"
            role="tab"
            aria-selected={netIdx === i}
            onClick={() => setNetIdx(i)}
            style={{ ...tabStyles.tabBtn, ...(netIdx === i ? tabStyles.tabActive : {}) }}
          >
            {n.label}
          </button>
        ))}
      </div>

      {companies.length > 0 && (
        <div style={{ ...tabStyles.siteTabRow, marginTop: 8 }} role="tablist" aria-label="Компании">
          {companies.map((c, i) => {
            const brand =
              c.company_id === 'sna' ? 'sna'
                : c.company_id === 'prolife' ? 'prolife'
                  : c.company_id === 'plas' ? 'plas'
                    : '';
            return (
              <button
                key={c.company_id}
                type="button"
                role="tab"
                aria-selected={coIdx === i}
                onClick={() => setCoIdx(i)}
                className={`brand-chip${brand ? ` brand-chip--${brand}` : ''}${coIdx === i ? ' brand-chip--active' : ''}`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <SocialCompanyView company={company} />
      </div>
    </div>
  );
}

const socStyles = {
  period: {
    color: 'var(--text-secondary)',
    fontSize: 13,
    marginBottom: 14,
  },
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: 12,
  },
  metricCard: {
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '14px 16px',
    boxShadow: 'var(--card-rest)',
  },
  metricName: {
    color: 'var(--text-muted)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  metricValue: {
    color: 'var(--text-primary)',
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  metricDelta: {
    marginTop: 4,
    color: 'var(--success)',
    fontSize: 12,
    fontWeight: 600,
  },
  note: {
    marginTop: 14,
    color: 'var(--text-muted)',
    fontSize: 12,
    lineHeight: 1.45,
  },
  source: {
    marginTop: 12,
    color: 'var(--text-muted)',
    fontSize: 11,
  },
};

// ── Instagram Panel ──────────────────────────────────────────────────────────

const INSTA_CHART_ROWS = ['Просмотры', 'Охваты', 'Взаимодействия', 'Просмотры постов', 'Лайки'];

/** Метрики для графика динамики в зависимости от набора показателей сети. */
function pickSocialChartRows(rows) {
  const list = rows || [];
  const names = list.map((r) => String(r.name || '').trim());
  const has = (pred) => names.some(pred);
  const pick = (pred) => list.filter((r) => pred(String(r.name || '').trim()));

  // Пинтерест: показы, общая аудитория, вовлечение
  if (has((n) => /^показы$/i.test(n)) || has((n) => /^общая\s*ауд/i.test(n))) {
    const order = [
      (n) => /^показы$/i.test(n),
      (n) => /^общая\s*ауд/i.test(n),
      (n) => /^вовлечение$/i.test(n),
    ];
    const selected = [];
    order.forEach((pred) => {
      const row = list.find((r) => pred(String(r.name || '').trim()));
      if (row) selected.push(row);
    });
    if (selected.length) return selected;
  }

  // Instagram / PLAS monthly — если есть классические метрики IN
  const insta = list.filter((r) => INSTA_CHART_ROWS.includes(r.name));
  if (insta.length) return insta;

  // Телеграм / ВК (SNA, Prolife): подписчики + охват
  if (
    has((n) => /^подписчик/i.test(n))
    && has((n) => /^охват/i.test(n))
  ) {
    const selected = [];
    const subs = list.find((r) => /^подписчик/i.test(String(r.name || '').trim()));
    if (subs) selected.push(subs);
    const reach = list.find((r) => /^охват/i.test(String(r.name || '').trim()));
    if (reach) selected.push(reach);
    if (selected.length) return selected;
  }

  // Фоллбек: первые числовые ряды (без ER/ERR %), макс. 4
  return pick((n) => !/^er\b/i.test(n) && !/^err\b/i.test(n)).slice(0, 4);
}

function pickSocialKpiNames(rows) {
  const names = (rows || []).map((r) => String(r.name || '').trim());
  if (names.some((n) => /^показы$/i.test(n))) {
    return ['Показы', 'Общая ауд', 'Вовлечение', 'Активная ауд', 'Сохранения'];
  }
  if (names.some((n) => INSTA_CHART_ROWS.includes(n))) {
    return ['Подписчики', 'Просмотры', 'Охваты', 'Взаимодействия', 'Прирост'];
  }
  if (names.some((n) => /^подписчик/i.test(n)) && names.some((n) => /^охват/i.test(n))) {
    const reachName = names.find((n) => /^охват/i.test(n));
    return ['Подписчики', reachName, 'Просмотры контента', 'Посещения', 'ER', 'ERR (сколько читают канал)'].filter(Boolean);
  }
  return ['Подписчики', 'Просмотры', 'Охваты', 'Взаимодействия', 'Прирост'];
}

function InstagramPanel({ data }) {
  const { theme } = useTheme();
  const instaChartColors = ['var(--accent)', theme.chartColors[1], theme.chartColors[3], theme.chartColors[4], theme.chartColors[5]];
  const accounts = data.accounts?.length
    ? data.accounts
    : [{ name: 'SNA', months: data.months || [], rows: data.rows || [] }];
  const [activeTab, setActiveTab] = useState(0);
  const account = accounts[activeTab] || accounts[0];
  const { months, rows } = account;
  const lastIdx = months.length - 1;

  // Build chart data: one point per month, keys = metric names
  const chartRows = pickSocialChartRows(rows);
  const chartData = months.map((month, i) => {
    const pt = { date: month };
    chartRows.forEach((r) => { pt[r.name] = r.values[i] ?? null; });
    return pt;
  });
  const chartLines = chartRows.map((r, i) => ({
    key: r.name,
    name: r.name,
    color: instaChartColors[i] || theme.chartColors[i],
  }));

  // KPI cards from last month
  const KPI_METRICS = pickSocialKpiNames(rows);
  const kpis = KPI_METRICS.map((name) => {
    const row = rows.find((r) => r.name === name)
      || rows.find((r) => String(r.name || '').trim().toLowerCase() === String(name || '').toLowerCase());
    const val = row ? row.values[lastIdx] : null;
    const prev = row ? row.values[lastIdx - 1] : null;
    const isEr = /^er\b/i.test(name) || /^err\b/i.test(name);
    const delta = (!isEr && name !== 'Прирост' && val != null && prev != null && prev !== 0)
      ? Math.round((val - prev) / prev * 100 * 10) / 10
      : null;
    return { name: row?.name || name, val, delta, isEr };
  }).filter((k) => k.val != null);

  const fmtVal = (v, isPct) => {
    if (v == null) return '—';
    if (isPct) return `${(v * 100).toFixed(1)}%`;
    return Number.isInteger(v) ? v.toLocaleString('ru-RU') : v.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
  };

  return (
    <div>
      {accounts.length > 1 && (
        <div style={tabStyles.siteTabRow} role="tablist" aria-label="Аккаунты Instagram">
          {accounts.map((acc, i) => (
            <button
              key={acc.name || i}
              type="button"
              role="tab"
              aria-selected={activeTab === i}
              onClick={() => setActiveTab(i)}
              style={{ ...tabStyles.tabBtn, ...(activeTab === i ? tabStyles.tabActive : {}) }}
            >
              {acc.name}
            </button>
          ))}
        </div>
      )}

      {/* KPI strip */}
      <div style={iStyles.kpiRow}>
        {kpis.map((k) => (
          <div key={k.name} style={iStyles.kpiBox}>
            <span style={iStyles.kpiLabel}>{k.name} ({months[lastIdx]})</span>
            <span style={iStyles.kpiValue}>
              {k.isEr ? fmtEr(k.val) : k.val.toLocaleString('ru-RU')}
            </span>
            {k.delta != null && (
              <span style={{ ...iStyles.kpiDelta, color: k.delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {k.delta >= 0 ? '+' : ''}{k.delta}%
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Trend chart */}
      {chartData.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={iStyles.subHead}>Динамика</h3>
          <TimeSeriesChart data={chartData} lines={chartLines} height={280} />
        </div>
      )}

      {/* Full metrics table */}
      <h3 style={iStyles.subHead}>Все показатели по месяцам</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={iStyles.table}>
          <thead>
            <tr>
              <th style={iStyles.th}>Показатель</th>
              {months.map((m) => <th key={m} style={{ ...iStyles.th, textAlign: 'right' }}>{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                <td style={iStyles.td}>{row.name}</td>
                {row.values.map((v, ci) => (
                  <td key={ci} style={iStyles.tdVal}>{fmtVal(v, row.is_pct)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const iStyles = {
  kpiRow: { display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24 },
  kpiBox: { display: 'flex', flexDirection: 'column', gap: 3 },
  kpiLabel: { color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 },
  kpiValue: { color: 'var(--accent)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  kpiDelta: { fontSize: 12, fontWeight: 600 },
  subHead: { color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: 0.4 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    background: 'var(--bg-input)', color: 'var(--text-muted)',
    padding: '10px 12px', borderBottom: '1px solid var(--border)',
    fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
    whiteSpace: 'nowrap',
  },
  td: { padding: '9px 12px', borderBottom: `1px solid var(--border)`, color: 'var(--text-secondary)', whiteSpace: 'nowrap' },
  tdVal: { padding: '9px 12px', borderBottom: `1px solid var(--border)`, color: 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
};

// ─────────────────────────────────────────────────────────────────────────────

const TRAINING_GOAL_MATCHERS = [
  /записаться на обучение/i,
  /заполнение формы обучение/i,
];

function isTrainingGoal(name) {
  return TRAINING_GOAL_MATCHERS.some((re) => re.test(name || ''));
}

function WebAnalyticsTabs({ sites }) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  if (!sites || sites.length === 0) return null;
  const site = sites[activeTab] || sites[0];

  // Detect new format (has `months` array) vs old Metrika format
  const isNewFormat = Array.isArray(site.months);

  if (!isNewFormat) {
    // Legacy Metrika-DB format
    return (
      <div>
        <div style={tabStyles.siteTabRow} role="tablist" aria-label="Сайты">
          {sites.map((s, i) => (
            <button
              key={s.site || i}
              type="button"
              role="tab"
              aria-selected={activeTab === i}
              onClick={() => setActiveTab(i)}
              style={{ ...tabStyles.tabBtn, ...(activeTab === i ? tabStyles.tabActive : {}) }}
            >
              {s.tab_name || s.site}
            </button>
          ))}
        </div>
        <div style={tabStyles.siteCard}>
          <div style={tabStyles.metricsRow}>
            <div style={tabStyles.metricBox}><span style={tabStyles.metricLabel}>Визиты</span><span style={tabStyles.metricValue}>{(site.visits || 0).toLocaleString('ru-RU')}</span></div>
            <div style={tabStyles.metricBox}><span style={tabStyles.metricLabel}>Просмотры</span><span style={tabStyles.metricValue}>{(site.page_views || 0).toLocaleString('ru-RU')}</span></div>
            <div style={tabStyles.metricBox}><span style={tabStyles.metricLabel}>Посетители</span><span style={tabStyles.metricValue}>{(site.visitors || 0).toLocaleString('ru-RU')}</span></div>
          </div>
          {(site.goals || []).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Цели</h3>
              <table style={tabStyles.goalsTable}>
                <thead>
                  <tr>
                    <th style={tabStyles.goalTh}>Цель</th>
                    <th style={{ ...tabStyles.goalTh, textAlign: 'right' }}>Достижения</th>
                  </tr>
                </thead>
                <tbody>
                  {site.goals.map((g, gi) => (
                    <tr key={gi}>
                      <td style={tabStyles.goalTd}>{g.name}</td>
                      <td style={{ ...tabStyles.goalTd, textAlign: 'right', fontWeight: 600 }}>{g.completions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // New format: rich monthly breakdown from Счётчики.xlsx
  const months = site.months || [];
  const pageViews = site.page_views || [];
  const visitors = site.visitors || [];
  const sources = site.traffic_sources || [];
  const goals = site.goals || [];
  const isSnaSite = (site.site || site.tab_name || '').toLowerCase() === 'sna';
  const trainingGoals = isSnaSite ? goals.filter((g) => isTrainingGoal(g.name)) : [];
  const otherGoals = isSnaSite ? goals.filter((g) => !isTrainingGoal(g.name)) : goals;

  const fmt = (v) => (v != null ? v.toLocaleString('ru-RU') : '—');

  const GoalRows = ({ items }) => items.map((g, gi) => (
    <tr key={gi}>
      <td style={tabStyles.mTd}>{g.name}</td>
      {g.values.map((v, ci) => (
        <td key={ci} style={tabStyles.mTdVal}>{fmt(v)}</td>
      ))}
    </tr>
  ));

  const GoalsSection = ({ items, subTitle }) => (
    <>
      {subTitle && (
        <tr>
          <td
            colSpan={months.length + 1}
            style={tabStyles.mSubHeading}
          >
            {subTitle}
          </td>
        </tr>
      )}
      <GoalRows items={items} />
    </>
  );

  const MonthlyTable = ({ title, headerRows, dataRows, accent }) => (
    <div style={{ marginBottom: 22 }}>
      <h3 style={tabStyles.subHeading}>{title}</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={tabStyles.monthTable}>
          <thead>
            <tr>
              <th style={tabStyles.mTh}>Метрика / источник</th>
              {months.map((m) => <th key={m} style={{ ...tabStyles.mTh, textAlign: 'right' }}>{m}</th>)}
            </tr>
          </thead>
          <tbody>
            {headerRows.map(({ label, values }, ri) => (
              <tr key={ri}>
                <td style={{ ...tabStyles.mTd, fontWeight: 600, color: accent || 'var(--text-primary)' }}>{label}</td>
                {values.map((v, ci) => (
                  <td key={ci} style={{ ...tabStyles.mTdVal, fontWeight: 600 }}>{fmt(v)}</td>
                ))}
              </tr>
            ))}
            {dataRows.map(({ label, values }, ri) => (
              <tr key={`d${ri}`}>
                <td style={tabStyles.mTd}>{label}</td>
                {values.map((v, ci) => (
                  <td key={ci} style={tabStyles.mTdVal}>{fmt(v)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Build top-level summary KPIs from last available month
  const lastIdx = months.length - 1;
  const kpis = [
    { label: 'Просмотры', value: pageViews[lastIdx] },
    { label: 'Посетители', value: visitors[lastIdx] },
  ].filter((k) => k.value != null);

  const siteKey = String(site.site || site.tab_name || activeTab).replace(/\W+/g, '_');

  const trafficChartData = months.map((m, i) => ({
    date: m,
    page_views: pageViews[i] ?? null,
    visitors: visitors[i] ?? null,
  }));

  const goalsChartData = months.map((m, i) => {
    const pt = { date: m };
    goals.forEach((g, gi) => {
      pt[`goal_${gi}`] = g.values?.[i] ?? null;
    });
    return pt;
  });

  const goalsChartLines = goals.map((g, gi) => ({
    key: `goal_${gi}`,
    name: g.name,
    color: theme.chartColors[gi % theme.chartColors.length],
  }));

  return (
    <div>
      {/* Site tabs */}
      <div style={tabStyles.siteTabRow} role="tablist" aria-label="Сайты">
        {sites.map((s, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={activeTab === i}
            onClick={() => setActiveTab(i)}
            style={{ ...tabStyles.tabBtn, ...(activeTab === i ? tabStyles.tabActive : {}) }}
          >
            {s.tab_name || s.site}
          </button>
        ))}
      </div>

      {/* KPI row — last month summary */}
      {kpis.length > 0 && (
        <div style={tabStyles.kpiStrip}>
          {kpis.map((k) => (
            <div key={k.label} style={tabStyles.kpiBox}>
              <span style={tabStyles.kpiLabel}>{k.label} ({months[lastIdx]})</span>
              <span style={tabStyles.kpiValue}>{k.value.toLocaleString('ru-RU')}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 22 }}>
        <h3 style={tabStyles.subHeading}>Просмотры и посетители</h3>
        <TimeSeriesChart
          idPrefix={`wa-traffic-${siteKey}`}
          data={trafficChartData}
          height={300}
          lines={[
            { key: 'page_views', name: 'Просмотры', color: 'var(--accent)' },
            { key: 'visitors', name: 'Посетители', color: 'var(--success)' },
          ]}
        />
      </div>

      {goalsChartLines.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <h3 style={tabStyles.subHeading}>Целевые действия</h3>
          <TimeSeriesChart
            idPrefix={`wa-goals-${siteKey}`}
            data={goalsChartData}
            height={Math.min(420, 280 + goalsChartLines.length * 12)}
            lines={goalsChartLines}
          />
        </div>
      )}

      {/* Traffic: просмотры + посетители + sources */}
      <MonthlyTable
        title="Трафик"
        headerRows={[
          ...(pageViews.some((v) => v != null) ? [{ label: 'Просмотры', values: pageViews }] : []),
          ...(visitors.some((v) => v != null) ? [{ label: 'Посетители', values: visitors }] : []),
        ]}
        dataRows={sources.map((s) => ({ label: s.name, values: s.values }))}
        accent={'var(--accent)'}
      />

      {/* Goals */}
      {goals.length > 0 && (
        <div style={{ marginBottom: 22 }}>
          <h3 style={tabStyles.subHeading}>Целевые действия — таблица</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={tabStyles.monthTable}>
              <thead>
                <tr>
                  <th style={tabStyles.mTh}>Цель</th>
                  {months.map((m) => <th key={m} style={{ ...tabStyles.mTh, textAlign: 'right' }}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {isSnaSite && trainingGoals.length > 0 && (
                  <GoalsSection items={trainingGoals} subTitle="Заявки на обучение" />
                )}
                <GoalRows items={otherGoals} />
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: 1400, margin: '0 auto', paddingBottom: 8 },
  pageHead: {
    marginBottom: 20,
  },
  h1: {
    color: 'var(--text-primary)',
    fontSize: 26,
    fontWeight: 700,
    margin: 0,
    letterSpacing: '-0.03em',
    fontFamily: 'var(--font-display)',
  },
  sectionTitle: {
    color: 'var(--text-primary)',
    fontSize: 15,
    fontWeight: 650,
    margin: '0 0 14px 0',
    fontFamily: 'var(--font-display)',
    letterSpacing: '-0.01em',
  },
  emptyState: {
    color: 'var(--text-muted)',
    fontSize: 13,
    padding: '20px 0',
    lineHeight: 1.5,
  },
  kpiRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 },
  tabs: {
    display: 'flex', gap: 4, marginBottom: 24, flexWrap: 'wrap',
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)', borderRadius: 14, padding: 5,
    boxShadow: 'var(--shadow-sm)',
  },
  tabBtn: {
    background: 'transparent', color: 'var(--text-secondary)',
    border: 'none', borderRadius: 10,
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 500,
    transition: 'transform 0.18s ease, background 0.2s, color 0.2s, box-shadow 0.2s',
  },
  tabActive: {
    background: 'var(--accent-dim)', color: 'var(--accent)',
    fontWeight: 600, boxShadow: 'var(--shadow-sm)',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
    gap: 18,
    marginBottom: 18,
  },
  section: {
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: 22,
    marginBottom: 18,
  },
  modeBtn: {
    background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
  },
  modeBtnActive: { background: 'var(--accent-dim)', color: 'var(--accent)', borderColor: 'var(--accent)' },
  imTableScroll: { overflowX: 'auto', maxWidth: '100%' },
  imTable: { width: 'max-content', minWidth: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13 },
  imTh: {
    background: 'var(--bg-input)', color: 'var(--text-muted)', padding: '12px 14px',
    borderBottom: '1px solid var(--border)', fontWeight: 600, textAlign: 'center',
    whiteSpace: 'nowrap', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
  },
  imThSticky: {
    position: 'sticky', left: 0, zIndex: 3,
    background: 'var(--bg-card)', textAlign: 'left', minWidth: 220,
    boxShadow: '4px 0 10px color-mix(in srgb, var(--text-primary) 8%, transparent)',
  },
  imTd: { padding: '10px 14px', borderBottom: `1px solid var(--border)`, color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap' },
  imTdSticky: {
    position: 'sticky', left: 0, zIndex: 2,
    background: 'var(--bg-card)', minWidth: 220,
    boxShadow: '4px 0 10px color-mix(in srgb, var(--text-primary) 8%, transparent)',
  },
  imTdVal: { padding: '10px 14px', borderBottom: `1px solid var(--border)`, color: 'var(--text-primary)', textAlign: 'center', fontVariantNumeric: 'tabular-nums' },
};

const leadsStyles = {
  sourceHint: {
    color: 'var(--text-muted)',
    fontSize: 12,
    marginBottom: 12,
    lineHeight: 1.4,
  },
  dateBar: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 18,
    background: 'var(--surface-sheen), var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '14px 18px',
    marginBottom: 18,
    boxShadow: 'var(--card-rest)',
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: '1 1 320px',
    minWidth: 0,
  },
  dateLabel: {
    color: 'var(--text-muted)',
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  dateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  dateInput: {
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '8px 12px',
    fontSize: 13,
    outline: 'none',
    colorScheme: 'var(--color-scheme)',
  },
  arrow: { color: 'var(--text-muted)', fontSize: 14, userSelect: 'none' },
  presetGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
  },
  presetBtn: {
    background: 'var(--bg-input)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all .15s',
  },
};

const tabStyles = {
  siteTabRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
  },
  tabBtn: {
    background: 'transparent', color: 'var(--text-secondary)',
    border: 'none', borderRadius: 8,
    padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 500,
    transition: 'all .15s',
  },
  tabActive: { background: 'var(--accent-dim)', color: 'var(--accent)' },
  siteCard: {
    background: 'var(--bg-input)', borderRadius: 12, padding: 18,
    border: '1px solid var(--border)',
  },
  metricsRow: { display: 'flex', gap: 20, marginBottom: 10, flexWrap: 'wrap' },
  metricBox: { display: 'flex', flexDirection: 'column', gap: 3 },
  metricLabel: { color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 },
  metricValue: { color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  goalsTable: { width: '100%', borderCollapse: 'collapse' },
  goalTh: { color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.5 },
  goalTd: { color: 'var(--text-primary)', fontSize: 13, padding: '8px 10px', borderBottom: `1px solid var(--border)` },
  // new monthly-table styles
  subHeading: {
    color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
    margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: 0.4,
  },
  mSubHeading: {
    padding: '12px 12px 6px',
    color: 'var(--accent)',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    borderBottom: `1px solid rgba(34,211,238,.2)`,
    background: 'rgba(34,211,238,.06)',
  },
  monthTable: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  mTh: {
    background: 'var(--bg-input)', color: 'var(--text-muted)',
    padding: '10px 12px', borderBottom: '1px solid var(--border)',
    fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4,
    whiteSpace: 'nowrap',
  },
  mTd: {
    padding: '9px 12px', borderBottom: `1px solid var(--border)`,
    color: 'var(--text-secondary)', whiteSpace: 'nowrap',
  },
  mTdVal: {
    padding: '9px 12px', borderBottom: `1px solid var(--border)`,
    color: 'var(--text-primary)', textAlign: 'right',
    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
  },
  kpiStrip: {
    display: 'flex', gap: 20, flexWrap: 'wrap',
    marginBottom: 20,
    padding: '14px 18px',
    background: 'var(--bg-input)',
    borderRadius: 10,
    border: '1px solid var(--border)',
  },
  kpiBox: { display: 'flex', flexDirection: 'column', gap: 4 },
  kpiLabel: { color: 'var(--text-muted)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 },
  kpiValue: { color: 'var(--accent)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
};
